import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Save,
  Close,
  ContentCopy,
  OpenInNew,
  Settings,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import config from "../../../config/config.json";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Loading from "../../loading";
 

export default function AgentManagement() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dataSets] = useState([
    { id: "1", name: "2025-2026 Academic Year", year: "2025-2026" },
    { id: "2", name: "2024-2025 Academic Year", year: "2024-2025" },
    { id: "3", name: "2026-2027 Academic Year", year: "2026-2027" },
  ]);

  const [editingAgent, setEditingAgent] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDialogOpen2, setIsDialogOpen2] = useState(false);
  const [editingAgent2, setEditingAgent2] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  useEffect(() => {
    fetchAgents();
  }, [token]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.base_url}/api/admin/agents`, {
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        // Unauthorized: trigger logout
        console.log("Unauthorized access, logging out...");
        logout();  
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch agents");
      }

      const data = await response.json();

      setAgents(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching agents:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAgent = () => {
    setEditingAgent({
      name: "",
      email: "",
      phone: "",
      status: "active",
      assignedDataSets: [],
      calculatorUrl: "",
      password: "",
      createdAt: new Date().toISOString().split("T")[0],
    });
    setIsDialogOpen(true);
    setValidationErrors({});
  };

  const handleEditAgent = (agent) => {
    setEditingAgent({ ...agent, email: agent.Users?.[0]?.email || "" });
    setIsDialogOpen2(true);
  };

  const handleAgentFieldChange = (field, value) => {
    setEditingAgent((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
  };

  const handleSaveAgent = async () => {
    if (!editingAgent) return;

    const urlSlug = uuidv4();

    const updatedAgent = {
      ...editingAgent,
      baseUrl: `${config.self_url}/calculator/${urlSlug}`,
      id: urlSlug,
    };

    const errors = validateAll(updatedAgent);
    setValidationErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    try {
      // First, register the user
      const userData = {
        email: updatedAgent.email,
        name: updatedAgent.name,
        password: updatedAgent.password,
      };

      const userResponse = await fetch(`${config.base_url}/api/auth/register`, {
        method: "POST",
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      if (userResponse.status === 401) {
        // Unauthorized: trigger logout
        console.log("Unauthorized access, logging out...");
        logout();  
        return;
      }

      if (!userResponse.ok) {
        throw new Error("Failed to register user");
      }

      const userResult = await userResponse.json();
      console.log("userResult", userResult);

      // Then create/update the agent with the user ID
      const method = agents.some((a) => a.id === updatedAgent.id)
        ? "PUT"
        : "POST";
      const url =
        method === "PUT"
          ? `${config.base_url}/api/admin/agents/${updatedAgent.id}`
          : `${config.base_url}/api/admin/agents`;

      const agentData = {
        ...updatedAgent,
        userId: userResult.user.id, // Add the user ID to the agent data
      };

      const agentResponse = await fetch(url, {
        method,
        headers: {
          Authorization: `${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(agentData),
      });

      if (!agentResponse.ok) {
        // If agent creation fails, we should probably delete the user
        await fetch(`${config.base_url}/api/admin/users/${userResult.id}`, {
          method: "DELETE",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        });
        throw new Error("Failed to save agent");
      }

      const agentResult = await agentResponse.json();

      // Refresh the agents list after successful save
      await fetchAgents();
      setEditingAgent(null);
      setIsDialogOpen(false);

      // If this was a new agent creation (not an edit), navigate to ManageAgentData
      if (method === "POST") {
        navigate(`/admin/agent-data/${agentResult.id}`);
      }
    } catch (err) {
      console.error("Error saving agent:", err);
      // You might want to show an error message to the user here
    }
  };

  const handleUpdateAgent = async () => {
    if (!editingAgent?.id) return;

    try {
      const response = await fetch(
        `${config.base_url}/api/admin/agents/${editingAgent.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editingAgent),
        }
      );

      if (response.status === 401) {
        // Unauthorized: trigger logout
        console.log("Unauthorized access, logging out...");
        logout();  
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to update agent");
      }

      // ✅ Refresh agents list after successful update
      await fetchAgents();

      // ✅ Close modal and clear edit state
      setIsDialogOpen2(false);
      setEditingAgent(null);
    } catch (error) {
      console.error("Update error:", error);
    }
  };

  const handleDeleteAgent = async (id) => {
    try {
      const response = await fetch(
        `${config.base_url}/api/admin/agents/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 401) {
        console.warn("401 Unauthorized - logging out...");
        logout(); // ✅ Call logout on unauthorized
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to delete agent");
      }

      // Refresh the agents list after successful deletion
      await fetchAgents();
    } catch (err) {
      console.error("Error deleting agent:", err);
      // You might want to show an error message to the user here
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
  };

  if (loading) {
    return (
      <Loading />
      // <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
      //   <CircularProgress />
      // </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  console.log(editingAgent2, "editingAgent");

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Agent URLs
          </Typography>
          <Typography color="text.secondary">Manage agent URLs</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddAgent}
          className="dashboard-btns"
        >
          Add URL
        </Button>
      </Box>

      <Card>
        <CardHeader title="Agents Overview" />
        <CardContent className="card-content">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Calculator URL</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{agent.name}</TableCell>
                  <TableCell>
                    {agent.Users[0]?.email ? agent.Users[0]?.email : "-"}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="caption"
                        sx={{
                          maxWidth: 180,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {agent.baseUrl}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyUrl(agent.baseUrl)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => window.open(agent.baseUrl, "_blank")}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                      <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/admin/agent-data/${agent.id}`)
                          }
                          title="Edit Fee Data"
                        >
                          <Settings fontSize="small" />
                        </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {agent.isDefault ? null : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleEditAgent(agent)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedAgentId(agent.id);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this agent?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteDialogOpen(false)}
            className="cancel-btn"
            startIcon={<Close />}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteAgent(selectedAgentId);
              setIsDeleteDialogOpen(false);
              setSelectedAgentId(null);
            }}
            className="delete-btn"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {editingAgent?.name ? "Edit Agent" : "Add URL"}
        </DialogTitle>
        <DialogContent dividers>
          {editingAgent && (
            <Grid container spacing={1} mt={1}>
              <Grid item xs={4}>
                <TextField
                  label="Full Name"
                  fullWidth
                  value={editingAgent.name}
                  onChange={(e) =>
                    handleAgentFieldChange("name", e.target.value)
                  }
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Email"
                  fullWidth
                  value={editingAgent.email}
                  onChange={(e) =>
                    handleAgentFieldChange("email", e.target.value)
                  }
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Password"
                  fullWidth
                  value={editingAgent.password}
                  onChange={(e) =>
                    handleAgentFieldChange("password", e.target.value)
                  }
                  error={!!validationErrors.password}
                  helperText={validationErrors.password}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Close />}
            onClick={() => setIsDialogOpen(false)}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            startIcon={<Save />}
            variant="contained"
            onClick={handleSaveAgent}
            className="save-btn"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDialogOpen2}
        onClose={() => setIsDialogOpen2(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Edit Agent</DialogTitle>
        <DialogContent dividers>
          {editingAgent && (
            <Grid container spacing={1} mt={1}>
              <Grid item xs={4}>
                <TextField
                  label="Full Name"
                  fullWidth
                  value={editingAgent?.name}
                  onChange={(e) =>
                    handleAgentFieldChange("name", e.target.value)
                  }
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Email"
                  fullWidth
                  value={editingAgent?.email}
                  onChange={(e) =>
                    setEditingAgent((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Password"
                  fullWidth
                  value={editingAgent.password}
                  onChange={(e) =>
                    handleAgentFieldChange("password", e.target.value)
                  }
                  error={!!validationErrors.password}
                  helperText={validationErrors.password}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Close />}
            onClick={() => setIsDialogOpen2(false)}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            startIcon={<Save />}
            variant="contained"
            onClick={handleUpdateAgent}
            className="save-btn"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Validation helpers
const validateField = (field, value) => {
  switch (field) {
    case "name":
      if (!value || value.trim().length < 2)
        return "Full Name is required (min 2 characters)";
      break;
    case "email":
      if (!value) return "Email is required";
      if (!/^\S+@\S+\.\S+$/.test(value)) return "Invalid email address";
      break;
    case "password":
      if (!value || value.length < 6)
        return "Password is required (min 6 characters)";
      break;
    default:
      return "";
  }
  return "";
};

const validateAll = (agent) => {
  const errors = {};
  errors.name = validateField("name", agent.name);
  errors.email = validateField("email", agent.email);
  errors.password = validateField("password", agent.password);
  errors.status = validateField("status", agent.status);
  return errors;
};
